import { useEffect, useRef, useCallback, useState } from "react";
import { checkSessionStatus, deactivateSessions } from "../axios/user";
import { useAppStore } from "../stores/appStore";
import {
  getSocketInstance,
  disconnectSocket,
  isServerDisconnectVerificationInFlight,
  cancelPendingSocketReconnects,
  verifySocketRegistration,
} from "../utils/socket";
import {
  isActiveSessionTab,
  forceClaimSession,
  clearMultiTabSessionState,
  getMultiTabUserId,
} from "../utils/multiTabDetection";
import { getClientInstanceId } from "../utils/clientInstanceId";
import { useAssessmentStore } from "../stores/assessmentStore";
import { isAdminRoute } from "../utils/isAdminRoute";
import {
  clearAuthToken,
  getAuthToken,
  getUserIdFromToken,
} from "../utils/authToken";
import useUserStore from "../stores/userStore";
import { useAuthStore } from "../stores/authStore";
import {
  isSessionActivationInFlight,
  setSessionActivationInFlight,
  waitForSocketConnect,
  waitForSocketDisconnect,
} from "../utils/sessionActivation";

const getCurrentSessionUserId = (): string | null =>
  useUserStore.getState().user?.id ||
  getMultiTabUserId() ||
  getUserIdFromToken() ||
  null;

const surrenderLocalContestSession = () => {
  const { clearStore, setError } = useAssessmentStore.getState();
  const { setIsAssessmentOpen, setIsBrowserLocked } = useAppStore.getState();
  clearStore();
  setError(null);
  setIsAssessmentOpen(false);
  void setIsBrowserLocked(false, false);
};

const SESSION_CHECK_LOCK_KEY = "session_check_lock";
const SESSION_CHECK_LAST_TIME_KEY = "session_check_last_time";
const SESSION_ACTIVATED_KEY = "session_activated";
const FIRST_CONNECTION_KEY = "first_connection_timestamp";
const SESSION_CHECK_INTERVAL = 10000;
const SESSION_CHECK_INTERVAL_DISCONNECTED = 1000;
const MIN_CHECK_INTERVAL = 10000;
const LOCK_TIMEOUT = 5000;
const BROADCAST_CHANNEL_NAME = "session_status_channel";

const refreshConfigAfterDeviceActivation = async () => {
  try {
    const { fetchConfig, setRefreshChat } = useAppStore.getState();
    await fetchConfig();
    await setRefreshChat(true);
  } catch (error) {
    console.error(
      "Failed to refresh user config after device activation:",
      error,
    );
  }
};

type CheckStatusMode =
  | "interval"
  | "visibility"
  | "externalActivation"
  | "manual";

interface CheckStatusOptions {
  force?: boolean;
  mode?: CheckStatusMode;
  skipActiveTabCheck?: boolean;
}

export const useSessionStatusCheck = () => {
  const {
    currentSocketId,
    setCurrentSocketId,
    deviceConflictModalOpen,
    setDeviceConflictModalOpen,
    setConnectionError,
  } = useAppStore();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);
  const lastCheckTimeRef = useRef<number>(0);
  const pendingCheckRef = useRef<boolean>(false);
  const tabIdRef = useRef<string>(
    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const checkStatusRef = useRef<
    ((sid?: string, options?: CheckStatusOptions) => Promise<void>) | null
  >(null);
  const startIntervalRef = useRef<(() => void) | null>(null);
  const isActivatingRef = useRef(false);
  const [isActivating, setIsActivating] = useState(false);
  const lastSuccessfulActivationRef = useRef<number>(0);
  const initialSessionCheckDoneRef = useRef(false);
  const socketListenersAttachedRef = useRef(false);
  const socketWaitIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const shouldMakeRequest = useCallback((): boolean => {
    if (!isActiveSessionTab()) {
      return false;
    }

    try {
      const lockData = sessionStorage.getItem(SESSION_CHECK_LOCK_KEY);
      if (lockData) {
        const { tabId, timestamp } = JSON.parse(lockData);
        const now = Date.now();

        if (tabId !== tabIdRef.current && now - timestamp < LOCK_TIMEOUT) {
          return false;
        }
      }

      sessionStorage.setItem(
        SESSION_CHECK_LOCK_KEY,
        JSON.stringify({ tabId: tabIdRef.current, timestamp: Date.now() })
      );
      return true;
    } catch (e) {
      return true;
    }
  }, []);

  const releaseLock = useCallback(() => {
    try {
      const lockData = sessionStorage.getItem(SESSION_CHECK_LOCK_KEY);
      if (lockData) {
        const { tabId } = JSON.parse(lockData);
        if (tabId === tabIdRef.current) {
          sessionStorage.removeItem(SESSION_CHECK_LOCK_KEY);
        }
      }
    } catch (e) {
      // Ignore errors
    }
  }, []);

  const checkStatus = useCallback(
    async (sid?: string, options: CheckStatusOptions = {}) => {
      // Skip device conflict checks for admin routes
      if (isAdminRoute()) {
        return;
      }

      const {
        force = false,
        mode = "manual",
        skipActiveTabCheck = false,
      } = options;

      const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();

      const timeSinceActivation =
        Date.now() - lastSuccessfulActivationRef.current;
      if (
        timeSinceActivation < 3000 &&
        mode !== "externalActivation" &&
        !hasConflict
      ) {
        return;
      }

      if (
        hasConflict &&
        mode !== "externalActivation" &&
        !isActivatingRef.current
      ) {
        pendingCheckRef.current = false;
        return;
      }

      const now = Date.now();

      if (!force && (mode === "interval" || mode === "visibility")) {
        if (now - lastCheckTimeRef.current < MIN_CHECK_INTERVAL) {
          pendingCheckRef.current = true;
          return;
        }
      }

      if (isCheckingRef.current) {
        return;
      }

      if (
        !skipActiveTabCheck &&
        (mode === "interval" || mode === "visibility")
      ) {
        if (!isActiveSessionTab()) {
          return;
        }
      }

      const needLock = mode === "interval" || mode === "visibility";

      if (needLock && !shouldMakeRequest()) {
        return;
      }

      if (isSessionActivationInFlight() || isActivatingRef.current) {
        if (mode !== "externalActivation") {
          return;
        }
      }

      let socketId = sid;

      if (!socketId) {
        const socket = getSocketInstance();
        if (socket && socket.connected && socket.id) {
          socketId = socket.id;
          if (socketId !== currentSocketId) {
            setCurrentSocketId(socketId);
          }
        }
        // Do NOT fall back to stale currentSocketId while disconnected —
        // a removed sid looks like "conflict" and falsely opens the modal
        // on a single device after reconnect races.
      } else {
        if (socketId !== currentSocketId) {
          setCurrentSocketId(socketId);
        }
      }

      // No sid yet: skip only while a connect is still expected. If we are
      // already disconnected, sid-less /session_status returns "conflict"
      // when another device holds the registered sid(s) — required for the
      // second-device Activate modal (backend rejects connect with return False).
      if (!socketId && mode !== "externalActivation" && mode !== "manual") {
        const liveSocket = getSocketInstance();
        if (liveSocket?.connected) {
          return;
        }
      }

      try {
        isCheckingRef.current = true;
        lastCheckTimeRef.current = Date.now();

        if (needLock) {
          try {
            sessionStorage.setItem(
              SESSION_CHECK_LAST_TIME_KEY,
              Date.now().toString()
            );
          } catch (e) {
            // Ignore
          }
        }

        const response = await checkSessionStatus(socketId);

        // Ignore a late status response for a connection that has already changed.
        if (socketId && getSocketInstance()?.id !== socketId) return;

        if (response.status === "unregistered") {
          if (!isSessionActivationInFlight()) {
            await verifySocketRegistration();
          }
          return;
        }

        if (response.status === "conflict") {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          pendingCheckRef.current = false;

          if (
            !isActivatingRef.current &&
            !isSessionActivationInFlight()
          ) {
            surrenderLocalContestSession();
            disconnectSocket();
            setCurrentSocketId(null);
            setDeviceConflictModalOpen(true);
            setConnectionError(null);
          }
        } else if (response.status === "active") {
          setConnectionError(null);

          const { deviceConflictModalOpen: modalOpen } = useAppStore.getState();
          if (
            !modalOpen &&
            !isServerDisconnectVerificationInFlight()
          ) {
            lastSuccessfulActivationRef.current = Date.now();

            const socket = getSocketInstance();
            if (
              socket &&
              socket.connected &&
              !intervalRef.current &&
              startIntervalRef.current
            ) {
              startIntervalRef.current();
            }
          }
        } else {
          const socket = getSocketInstance();
          const socketReady = Boolean(socket?.connected);

          if (!socketReady) {
            pendingCheckRef.current = true;
            return;
          }

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          const timeSinceActivation =
            Date.now() - lastSuccessfulActivationRef.current;
          const shouldPreventReopen = timeSinceActivation < 10000;

          if (
            !isActivatingRef.current &&
            !isSessionActivationInFlight() &&
            !deviceConflictModalOpen &&
            !shouldPreventReopen
          ) {
            surrenderLocalContestSession();
            setCurrentSocketId(null);
            setDeviceConflictModalOpen(true);
            setConnectionError(null);
          }
        }
      } catch (error) {
        console.error("Session status check error:", error);
      } finally {
        isCheckingRef.current = false;
        if (needLock) releaseLock();

        if (pendingCheckRef.current) {
          pendingCheckRef.current = false;
          const { deviceConflictModalOpen: hasConflict } =
            useAppStore.getState();

          const timeSinceActivation =
            Date.now() - lastSuccessfulActivationRef.current;
          const justActivated = timeSinceActivation < 5000;

          if (!hasConflict && !justActivated) {
            setTimeout(() => {
              if (!isCheckingRef.current && !isActivatingRef.current) {
                checkStatus(undefined, { mode, force: false });
              }
            }, MIN_CHECK_INTERVAL);
          }
        }
      }
    },
    [
      currentSocketId,
      setCurrentSocketId,
      deviceConflictModalOpen,
      setDeviceConflictModalOpen,
      setConnectionError,
      shouldMakeRequest,
      releaseLock,
    ]
  );

  useEffect(() => {
    checkStatusRef.current = checkStatus;
  }, [checkStatus]);

  const broadcastSessionActivated = useCallback((activationUserId: string | null) => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: "session_activated",
        tabId: tabIdRef.current,
        userId: activationUserId,
        timestamp: Date.now(),
      });
    }

    try {
      localStorage.setItem(
        SESSION_ACTIVATED_KEY,
        JSON.stringify({
          tabId: tabIdRef.current,
          userId: activationUserId,
          timestamp: Date.now(),
        }),
      );
      setTimeout(() => {
        try {
          const data = localStorage.getItem(SESSION_ACTIVATED_KEY);
          if (data) {
            const parsed = JSON.parse(data);
            if (parsed.tabId === tabIdRef.current) {
              localStorage.removeItem(SESSION_ACTIVATED_KEY);
            }
          }
        } catch {
          // Ignore
        }
      }, 1000);
    } catch {
      // Ignore
    }
  }, []);

  const waitUntilSessionActive = useCallback(async (sid?: string) => {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      try {
        const status = await checkSessionStatus(sid);
        if (status.status === "active" || (!sid && status.status === "unregistered")) {
          return;
        }
      } catch {
        // retry
      }
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    throw new Error(
      "Activation is taking longer than expected. Please try again.",
    );
  }, []);

  const handleActivateDevice = async () => {
    if (isActivatingRef.current || isSessionActivationInFlight()) {
      return;
    }

    isActivatingRef.current = true;
    setIsActivating(true);
    setSessionActivationInFlight(true);

    // Keep the conflict modal open until activation succeeds so the global
    // socket effect cannot race with a parallel connect/disconnect.
    surrenderLocalContestSession();
    setConnectionError(null);

    try {
      forceClaimSession();

      try {
        await deactivateSessions();
      } catch (deactivateError) {
        console.error("Failed to deactivate sessions:", deactivateError);
        // One retry — empty sid map is required for a clean claim.
        await deactivateSessions();
      }

      clearMultiTabSessionState(getCurrentSessionUserId() || undefined);
      forceClaimSession();

      try {
        sessionStorage.removeItem(SESSION_CHECK_LOCK_KEY);
        sessionStorage.removeItem(SESSION_CHECK_LAST_TIME_KEY);
      } catch {
        // Ignore errors
      }

      try {
        localStorage.setItem(FIRST_CONNECTION_KEY, Date.now().toString());
      } catch {
        // ignore
      }

      const socket = getSocketInstance();
      const activationUserId = getCurrentSessionUserId();

      if (!socket) {
        // Onboarding / fresh tabs: socket may not exist yet.
        await waitUntilSessionActive();
        lastSuccessfulActivationRef.current = Date.now();
        broadcastSessionActivated(activationUserId);
        await refreshConfigAfterDeviceActivation();
        setDeviceConflictModalOpen(false);
        setConnectionError(null);
        return;
      }

      cancelPendingSocketReconnects();
      // Disable auto-reconnect during the handoff so Socket.IO does not
      // invent a brief sid that the backend then immediately drops.
      if (socket.io?.opts) {
        socket.io.opts.reconnection = false;
      }
      await waitForSocketDisconnect(socket, 3000);
      // Let the backend clear the previous sid mapping before we claim a new one.
      await new Promise((resolve) => setTimeout(resolve, 400));

      const token = getAuthToken();
      if (!token) {
        throw new Error("Token is required");
      }

      await refreshConfigAfterDeviceActivation();
      const { focus, domain } = useAppStore.getState();

      socket.auth = {
        token,
        focus: focus || "",
        domain: domain || "",
      };

      if (socket.io?.opts) {
        socket.io.opts.reconnection = true;
      }

      const socketId = await waitForSocketConnect(socket, 10000);
      setCurrentSocketId(socketId);

      await waitUntilSessionActive(socketId);

      surrenderLocalContestSession();
      lastSuccessfulActivationRef.current = Date.now();
      broadcastSessionActivated(getCurrentSessionUserId());
      setDeviceConflictModalOpen(false);
      setConnectionError(null);
      // Connect during activation skipped handleConnect (modal was open).
      startIntervalRef.current?.();
    } catch (error: any) {
      const errorMessage =
        error?.response?.status === 404
          ? "Activation endpoint not found. Please refresh the page."
          : error?.message ||
            "Failed to activate device. Please try again or refresh the page.";

      setConnectionError(errorMessage);
      setDeviceConflictModalOpen(true);
      throw error;
    } finally {
      isActivatingRef.current = false;
      setIsActivating(false);
      setSessionActivationInFlight(false);
    }
  };

  const handleCancel = () => {
    setDeviceConflictModalOpen(false);
    if (disconnectSocket) {
      disconnectSocket();
    }

    // Local-only clear: do NOT call server logout — that would kill the
    // account that is still active in the other tab.
    try {
      clearMultiTabSessionState(getCurrentSessionUserId() || undefined);
      useUserStore.getState().clearUser();
      useUserStore.setState({ token: null });
      useAuthStore.getState().setAuthenticated(false);
      // clearAuthToken drops the shared cookie when it belongs to this tab
      // or no other tab's localStorage token remains.
      clearAuthToken();
    } catch {
      // ignore
    }

    window.location.assign("/login");
  };

  useEffect(() => {
    if (isAdminRoute()) {
      return;
    }

    const processNewConnection = async (socketId: string) => {
      setCurrentSocketId(socketId);

      getClientInstanceId();

      return true;
    };

    const startInterval = () => {
      // Skip interval checks for admin routes
      if (isAdminRoute()) {
        return;
      }

      const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();
      if (hasConflict) {
        return;
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();
        if (hasConflict) {
          return;
        }

        const currentSocket = getSocketInstance();
        const checkOptions = currentSocket?.connected
          ? isActiveSessionTab()
            ? { mode: "interval" as const }
            : {
                mode: "interval" as const,
                force: true,
                skipActiveTabCheck: true,
              }
          : {
              mode: "interval" as const,
              force: true,
              skipActiveTabCheck: true,
            };

        checkStatus(undefined, checkOptions);
      }, SESSION_CHECK_INTERVAL);
    };

    startIntervalRef.current = startInterval;

    const runInitialSessionCheck = async (sid?: string) => {
      if (initialSessionCheckDoneRef.current) {
        return;
      }

      initialSessionCheckDoneRef.current = true;
      await checkStatus(sid, {
        mode: "manual",
        force: true,
        skipActiveTabCheck: true,
      });
    };

    let socketConnectHandler: (() => void) | null = null;
    let socketDisconnectHandler: ((reason: string) => void) | null = null;

    const handleConnect = async (socket: NonNullable<ReturnType<typeof getSocketInstance>>) => {
      const sid = socket.id;
      if (!sid) return;

      const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();
      if (hasConflict) {
        return;
      }

      const ok = await processNewConnection(sid);
      if (!ok) return;

      await runInitialSessionCheck(sid);

      if (!useAppStore.getState().deviceConflictModalOpen) {
        startInterval();
      }
    };

    const handleDisconnect = (reason: string) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (isSessionActivationInFlight() || isActivatingRef.current) {
        return;
      }

      const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();
      if (!hasConflict && reason !== "io client disconnect") {
        // Backend restarts often surface as "io server disconnect". socket.ts
        // verifies via /session_status and reconnects.
        const serverDisconnectOwnedBySocket =
          reason === "io server disconnect" ||
          isServerDisconnectVerificationInFlight();

        if (!serverDisconnectOwnedBySocket) {
          checkStatus(undefined, {
            mode: "manual",
            force: true,
            skipActiveTabCheck: true,
          });
        }

        const disconnectedInterval = setInterval(() => {
          if (
            isServerDisconnectVerificationInFlight() ||
            isSessionActivationInFlight() ||
            isActivatingRef.current
          ) {
            return;
          }

          const { deviceConflictModalOpen: currentConflict } =
            useAppStore.getState();
          if (currentConflict) {
            clearInterval(disconnectedInterval);
            return;
          }

          const currentSocket = getSocketInstance();
          if (currentSocket && currentSocket.connected) {
            clearInterval(disconnectedInterval);
            startInterval();
            return;
          }

          // Sid-less check only — never pass a stale removed sid.
          checkStatus(undefined, {
            mode: "interval",
            force: true,
            skipActiveTabCheck: true,
          });
        }, SESSION_CHECK_INTERVAL_DISCONNECTED);

        intervalRef.current = disconnectedInterval as any;
      }
    };

    const attachSocketListeners = (
      socket: NonNullable<ReturnType<typeof getSocketInstance>>,
    ) => {
      if (socketListenersAttachedRef.current) {
        return;
      }

      socketListenersAttachedRef.current = true;

      socketConnectHandler = () => {
        void handleConnect(socket);
      };
      socketDisconnectHandler = (reason: string) => {
        handleDisconnect(reason);
      };

      socket.on("connect", socketConnectHandler);
      socket.on("disconnect", socketDisconnectHandler);

      if (socket.connected && socket.id) {
        void handleConnect(socket);
      }
    };

    const tryAttachSocketListeners = () => {
      const socket = getSocketInstance();
      if (socket) {
        attachSocketListeners(socket);
        if (socketWaitIntervalRef.current) {
          clearInterval(socketWaitIntervalRef.current);
          socketWaitIntervalRef.current = null;
        }
      }
    };

    tryAttachSocketListeners();
    if (!socketListenersAttachedRef.current) {
      socketWaitIntervalRef.current = setInterval(tryAttachSocketListeners, 100);
    }

    const handleVisibilityChange = () => {
      // Skip visibility checks for admin routes
      if (isAdminRoute()) {
        return;
      }

      if (document.visibilityState === "visible") {
        const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();
        if (hasConflict) {
          return;
        }

        checkStatus(undefined, {
          mode: "visibility",
          force: true,
          skipActiveTabCheck: !isActiveSessionTab(),
        });
      }
    };

    const handleWindowFocus = () => {
      // Skip focus checks for admin routes
      if (isAdminRoute()) {
        return;
      }

      const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();
      if (hasConflict) {
        return;
      }

      checkStatus(undefined, {
        mode: "visibility",
        force: true,
        skipActiveTabCheck: !isActiveSessionTab(),
      });
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === SESSION_CHECK_LAST_TIME_KEY &&
        e.newValue &&
        isActiveSessionTab()
      ) {
        const lastCheck = parseInt(e.newValue, 10);
        if (Date.now() - lastCheck < MIN_CHECK_INTERVAL) {
          lastCheckTimeRef.current = lastCheck;
        }
      }

      if (e.key === SESSION_ACTIVATED_KEY && e.newValue) {
        // Skip storage change checks for admin routes
        if (isAdminRoute()) {
          return;
        }

        try {
          const data = JSON.parse(e.newValue);
          const currentUserId = getCurrentSessionUserId();
          if (
            data.tabId !== tabIdRef.current &&
            data.userId &&
            currentUserId &&
            data.userId === currentUserId
          ) {
            surrenderLocalContestSession();
            disconnectSocket();
            setDeviceConflictModalOpen(true);
            setConnectionError(null);

            if (checkStatusRef.current) {
              setTimeout(() => {
                checkStatusRef.current?.(undefined, {
                  mode: "externalActivation",
                  force: true,
                  skipActiveTabCheck: true,
                });
              }, 100);
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    };

    if (typeof BroadcastChannel !== "undefined") {
      broadcastChannelRef.current = new BroadcastChannel(
        BROADCAST_CHANNEL_NAME
      );

      broadcastChannelRef.current.onmessage = (event) => {
        // Skip broadcast channel checks for admin routes
        if (isAdminRoute()) {
          return;
        }

        if (event.data.type === "session_activated") {
          const currentUserId = getCurrentSessionUserId();
          if (
            event.data.tabId !== tabIdRef.current &&
            event.data.userId &&
            currentUserId &&
            event.data.userId === currentUserId
          ) {
            surrenderLocalContestSession();
            disconnectSocket();
            setDeviceConflictModalOpen(true);
            setConnectionError(null);

            if (checkStatusRef.current) {
              setTimeout(() => {
                checkStatusRef.current?.(undefined, {
                  mode: "externalActivation",
                  force: true,
                  skipActiveTabCheck: true,
                });
              }, 100);
            }
          }
        }
      };
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleWindowFocus);

    const { deviceConflictModalOpen: hasConflict } = useAppStore.getState();
    if (!hasConflict) startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (socketWaitIntervalRef.current) {
        clearInterval(socketWaitIntervalRef.current);
        socketWaitIntervalRef.current = null;
      }
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
      releaseLock();

      const socket = getSocketInstance();
      if (socket && socketListenersAttachedRef.current) {
        if (socketConnectHandler) {
          socket.off("connect", socketConnectHandler);
        }
        if (socketDisconnectHandler) {
          socket.off("disconnect", socketDisconnectHandler);
        }
        socketListenersAttachedRef.current = false;
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [
    currentSocketId,
    checkStatus,
    releaseLock,
    setCurrentSocketId,
    setDeviceConflictModalOpen,
    setConnectionError,
  ]);

  return {
    handleActivateDevice,
    handleCancel,
    isActivating,
  };
};

